import React, {useMemo, useState} from "react";
import useCodeData from "../../hooks/useCodeData";
import SearchSelectorCategoryIcon from "./SearchSelectorCategoryIcon";

function ChevronIcon({open}) {
    return (
        <svg
            className={`search-selector-filter-chevron ${open ? "is-open" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );
}

function resolveFilterNodes(dataSet, config) {
    if (!dataSet) return [];

    const content = dataSet.content ?? dataSet;
    if (!content) return [];

    if (config.entity === "department") {
        return Array.isArray(content) ? content : [];
    }

    if (config.parentKey) {
        const matched = (Array.isArray(content) ? content : []).filter((each) => each.id === config.parentKey);
        return matched[0]?.children ?? [];
    }

    if (Array.isArray(content)) {
        return content;
    }

    return content?.children ?? [];
}

function getNodeLabel(node) {
    return node?.name ?? node?.title ?? node?.assetName ?? "";
}

function FilterTreeNodes({
    nodes,
    depth,
    target,
    selectedFilterKey,
    expandedNodeIds,
    onToggleNode,
    onSelectFilter,
}) {
    if (!nodes?.length) return null;

    return nodes.map((node) => {
        const nodeKey = `${target}:${node.id}`;
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const isExpanded = !!expandedNodeIds[nodeKey];
        const isSelected = selectedFilterKey === nodeKey;

        const label = getNodeLabel(node);

        return (
            <React.Fragment key={nodeKey}>
                <button
                    type="button"
                    className={[
                        "search-selector-filter-node",
                        isSelected ? "is-active" : "",
                    ].filter(Boolean).join(" ")}
                    style={{"--filter-depth": depth}}
                    title={label}
                    onClick={() => onSelectFilter(target, node)}
                >
                    {hasChildren ? (
                        <span
                            className="search-selector-filter-node-toggle"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleNode(nodeKey);
                            }}
                            role="presentation"
                        >
                            <ChevronIcon open={isExpanded}/>
                        </span>
                    ) : (
                        <span className="search-selector-filter-node-spacer"/>
                    )}
                    <span className="search-selector-filter-node-icon">
                        <SearchSelectorCategoryIcon type="folder"/>
                    </span>
                    <span className="search-selector-filter-node-label">{label}</span>
                </button>
                {hasChildren && isExpanded && (
                    <FilterTreeNodes
                        nodes={node.children}
                        depth={depth + 1}
                        target={target}
                        selectedFilterKey={selectedFilterKey}
                        expandedNodeIds={expandedNodeIds}
                        onToggleNode={onToggleNode}
                        onSelectFilter={onSelectFilter}
                    />
                )}
            </React.Fragment>
        );
    });
}

function getGroupIconType(entity) {
    if (entity === "department") return "department";
    if (entity === "company") return "company";
    return "folder";
}

function SearchSelectorFilterGroup({
    config,
    panelUniqueKey,
    selectedFilterKey,
    expandedGroupIds,
    expandedNodeIds,
    onToggleGroup,
    onToggleNode,
    onSelectFilter,
}) {
    const cacheKey = config.uniqueKey ?? `${panelUniqueKey}_${config.target}`;
    const {dataSet, isLoading} = useCodeData(config.entity, cacheKey);
    const nodes = useMemo(() => resolveFilterNodes(dataSet, config), [dataSet, config]);
    const groupKey = `group:${config.target}`;
    const isGroupExpanded = expandedGroupIds[groupKey] ?? false;
    const groupIconType = getGroupIconType(config.entity);

    return (
        <div
            className="search-selector-filter-section"
            data-entity={config.entity}
        >
            <button
                type="button"
                className="search-selector-filter-group"
                title={config.title}
                onClick={() => onToggleGroup(groupKey)}
            >
                <span
                    className="search-selector-filter-node-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleGroup(groupKey);
                    }}
                    role="presentation"
                >
                    <ChevronIcon open={isGroupExpanded}/>
                </span>
                <span className="search-selector-filter-group-icon">
                    <SearchSelectorCategoryIcon type={groupIconType}/>
                </span>
                <span className="search-selector-filter-group-label">{config.title}</span>
            </button>
            {isGroupExpanded && (
                <div className="search-selector-filter-section-body">
                    {isLoading ? (
                        <div className="search-selector-filter-loading">
                            불러오는 중...
                        </div>
                    ) : (
                        <FilterTreeNodes
                            nodes={nodes}
                            depth={0}
                            target={config.target}
                            selectedFilterKey={selectedFilterKey}
                            expandedNodeIds={expandedNodeIds}
                            onToggleNode={onToggleNode}
                            onSelectFilter={onSelectFilter}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function SearchSelectorFilterTree({
    filters,
    panelUniqueKey,
    selectedFilterKey,
    onSelectAll,
    onSelectFilter,
}) {
    const [expandedGroupIds, setExpandedGroupIds] = useState(() =>
        Object.fromEntries(filters.map((f) => [`group:${f.target}`, false])),
    );
    const [expandedNodeIds, setExpandedNodeIds] = useState({});

    const handleToggleGroup = (groupKey) => {
        setExpandedGroupIds((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    };

    const handleToggleNode = (nodeKey) => {
        setExpandedNodeIds((prev) => ({
            ...prev,
            [nodeKey]: !prev[nodeKey],
        }));
    };

    const handleSelectFilter = (target, node) => {
        onSelectFilter(target, node);

        if (Array.isArray(node.children) && node.children.length > 0) {
            setExpandedNodeIds((prev) => ({...prev, [`${target}:${node.id}`]: true}));
        }
    };

    if (!filters.length) return null;

    return (
        <aside className="search-selector-panel-sidebar">
            <div className="search-selector-panel-sidebar-content">
                <button
                    type="button"
                    className={[
                        "search-selector-filter-all",
                        selectedFilterKey === "all" ? "is-active" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={onSelectAll}
                    title="전체"
                >
                    <span className="search-selector-filter-node-icon">
                        <SearchSelectorCategoryIcon type="all"/>
                    </span>
                    <span className="search-selector-filter-node-label">전체</span>
                </button>

                {filters.map((config) => (
                    <SearchSelectorFilterGroup
                        key={config.target}
                        config={config}
                        panelUniqueKey={panelUniqueKey}
                        selectedFilterKey={selectedFilterKey}
                        expandedGroupIds={expandedGroupIds}
                        expandedNodeIds={expandedNodeIds}
                        onToggleGroup={handleToggleGroup}
                        onToggleNode={handleToggleNode}
                        onSelectFilter={handleSelectFilter}
                    />
                ))}
            </div>
        </aside>
    );
}

export default SearchSelectorFilterTree;
